import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const Terms: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Terms of Service — Sprout</title>
        <meta
          name="description"
          content="The contractual terms between Sprout and the schools that use it. Sets out Sprout's role as a POPIA operator under sections 20-21, the school's role as the responsible party, and the parental-consent warranty the school gives by signing up."
        />
        <link rel="canonical" href="https://sprout-ui-pi.vercel.app/legal/terms" />
      </Helmet>

      <article className="prose prose-sm dark:prose-invert max-w-3xl mx-auto px-4 py-12">
        <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
          Effective from 2026-04-29
        </p>
        <h1>Terms of Service</h1>
        <p className="lead">
          These Terms govern the use of Sprout (the "Service") by South African
          schools and educational organisations ("you", "the School"). They
          form a binding agreement between the School and Monolith AI (Pty)
          Ltd ("Sprout", "we"). They also constitute the written operator
          mandate required under sections 20 and 21 of the Protection of
          Personal Information Act, 2013 ("POPIA").
        </p>

        <h2>1. Roles under POPIA</h2>
        <p>
          The School is the <strong>responsible party</strong> for the personal
          information processed through Sprout. The School determines what
          information to record about each child, on what lawful basis, and
          for how long. Sprout is the <strong>operator</strong>. We process
          personal information only as instructed by the School, on the basis
          of these Terms, and only for the educational purposes the School
          uses Sprout for.
        </p>

        <h2>2. Parental consent warranty</h2>
        <p>
          By using Sprout, the School warrants and undertakes that, for every
          child whose information is entered into the Service:
        </p>
        <ul>
          <li>
            The School has obtained valid consent from a competent person
            (parent or legal guardian) under POPIA section 35(1) to process
            the child's personal information for the educational purposes the
            School uses Sprout for.
          </li>
          <li>
            That consent covers the categories of information actually
            recorded — identity details (name, date of birth), narrative
            observations, assessments, and (on paid plans where media uploads
            are enabled) photographs and short videos of the child.
          </li>
          <li>
            The School has explained to the parent or guardian that processing
            takes place via a third-party platform (Sprout), and that the
            parent has the right to access, correct, and delete that
            information through the School at any time.
          </li>
          <li>
            The School maintains evidence of that consent — on paper, in the
            School's admissions software, or recorded in Sprout's optional
            in-platform <code>Consent</code> table — and will produce that
            evidence on request.
          </li>
        </ul>
        <p>
          The School indemnifies Sprout against any claim, complaint, or
          regulatory action arising from the School's failure to obtain or
          maintain that consent.
        </p>

        <h2>3. What Sprout does and does not do</h2>
        <p>Sprout, as operator, will:</p>
        <ul>
          <li>
            Process the School's data only as the School instructs us to
            through normal use of the Service, or in writing where the
            instruction goes beyond ordinary use.
          </li>
          <li>
            Maintain the security safeguards described in our{' '}
            <Link to="/docs/security/popia">POPIA Compliance</Link> and{' '}
            <Link to="/docs/security/data-residency">Data Residency</Link>{' '}
            documentation: TLS in transit, encryption at rest, role-based
            access control, audit logs, and per-tenant schema isolation.
          </li>
          <li>
            Notify the School without undue delay of any confirmed security
            compromise affecting personal information processed for that
            School (POPIA section 22).
          </li>
          <li>
            Make personal information available for export at the School's
            request, in support of the data subject's right of access under
            POPIA section 23. See <Link to="/docs/security/data-export">Data Export</Link>.
          </li>
          <li>
            Act on requests for correction or deletion the School routes to
            us, including propagating deletions through backups within our
            standard backup retention window.
          </li>
        </ul>
        <p>Sprout will not:</p>
        <ul>
          <li>
            Use the School's data for our own commercial purposes, including
            training machine-learning models on observation text or sharing
            data with advertisers.
          </li>
          <li>
            Sub-process personal information through any third-party service
            not disclosed in our{' '}
            <Link to="/legal/subprocessors">subprocessor list</Link>.
          </li>
          <li>
            Use personal information for any purpose beyond the educational
            purposes for which the School engaged us — see "Purpose
            limitation" in <Link to="/docs/security/popia">POPIA Compliance</Link>.
          </li>
        </ul>

        <h2>4. Cross-border processing</h2>
        <p>
          Production data is hosted in the European Union (Supabase, Frankfurt)
          on infrastructure governed by Standard Contractual Clauses with
          Sprout, in line with POPIA section 72(1)(a). Schools that require
          South African-only data residency can{' '}
          <Link to="/contact">get in touch</Link> for a custom deployment.
        </p>

        <h2>5. Subscription and billing</h2>
        <p>
          Pricing is published on our <Link to="/pricing">Pricing</Link> page.
          The free Seedling plan is genuinely free; the paid Sprout plan is
          per-child metered above a 25-child minimum. Billing changes that
          materially affect the School will be communicated with at least 30
          days' notice.
        </p>

        <h2>6. Termination and data return</h2>
        <p>
          Either party may terminate this agreement on 30 days' written notice.
          On termination, the School can export the full data set via the
          per-child Data Export tool (or request a full-organisation export
          from us if needed). Within 90 days of termination, Sprout will delete
          the School's tenant data from production systems; backups expire
          within our standard backup retention window thereafter.
        </p>

        <h2>7. Limitation of liability</h2>
        <p>
          Sprout's aggregate liability arising from or relating to the Service
          is limited to the fees paid by the School in the 12 months preceding
          the claim, except in cases of gross negligence or wilful misconduct,
          or where applicable law requires otherwise. This limitation does
          not affect any data subject's rights under POPIA.
        </p>

        <h2>8. Changes to these Terms</h2>
        <p>
          We may update these Terms from time to time. Material changes will
          be communicated by email to organisation administrators at least 30
          days before they take effect. Continued use after the effective
          date constitutes acceptance.
        </p>

        <h2>9. Contact</h2>
        <p>
          Privacy and POPIA queries:{' '}
          <a href="mailto:privacy@sprout.education">privacy@sprout.education</a>.
          Contractual queries:{' '}
          <a href="mailto:hello@sprout.education">hello@sprout.education</a>{' '}
          or via our <Link to="/contact">contact page</Link>.
        </p>

        <h2>10. Governing law</h2>
        <p>
          These Terms are governed by the laws of the Republic of South Africa.
          Any dispute will be referred to the South African courts.
        </p>
      </article>
    </>
  );
};

export default Terms;
